import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import Button from '../common/Button';
import Card from '../common/Card';

export default function UserManagement({ escolaId }) {
    const [users, setUsers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [gates, setGates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        perfil: 'monitor',
        portao_id: ''
    });
    const [adding, setAdding] = useState(false);
    const [notification, setNotification] = useState(null);

    useEffect(() => {
        if (escolaId) {
            loadData();
        }
    }, [escolaId]);

    const loadData = async () => {
        try {
            setLoading(true);

            // 1. Carregar Portões
            const { data: gateData } = await supabase
                .from('portoes')
                .select('*')
                .eq('escola_id', escolaId);
            setGates(gateData || []);

            // 2. Carregar Usuários
            const { data: userData, error: userError } = await supabase
                .from('usuarios')
                .select('*, portao:portoes(nome)')
                .eq('escola_id', escolaId)
                .in('tipo', ['escola']);

            if (userError) throw userError;
            setUsers(userData || []);

        } catch (error) {
            console.error('Erro ao carregar dados da equipe:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddMember = async (e) => {
        e.preventDefault();
        if (!formData.email) return;

        try {
            setAdding(true);
            const { data, error } = await supabase.rpc('adicionar_membro_equipe', {
                email_busca: formData.email.toLowerCase().trim(),
                perfil_atribuido: formData.perfil
            });

            if (error) throw error;

            if (data.status === 'success') {
                // Se foi adicionado com sucesso, agora vinculamos o portão se houver um selecionado
                if (formData.portao_id) {
                    await supabase
                        .from('usuarios')
                        .update({ portao_id: formData.portao_id })
                        .eq('email', formData.email.toLowerCase().trim());
                }

                setNotification({ type: 'success', message: data.message });
                setFormData({ email: '', perfil: 'monitor', portao_id: '' });
                setTimeout(() => {
                    setShowAddForm(false);
                    setNotification(null);
                }, 4000);
                loadData();
            } else {
                setNotification({ type: 'error', message: data.message });
            }
        } catch (error) {
            console.error('Erro ao adicionar membro:', error);
            setNotification({ type: 'error', message: 'Erro ao processar solicitação.' });
        } finally {
            setAdding(false);
        }
    };

    const handleUpdateGate = async (userId, gateId) => {
        try {
            const { error } = await supabase
                .from('usuarios')
                .update({ portao_id: gateId || null })
                .eq('id', userId);

            if (error) throw error;

            // Feedback visual imediato
            setNotification({ type: 'success', message: 'Portão atualizado com sucesso!' });
            setTimeout(() => setNotification(null), 3000);

            loadData();
        } catch (error) {
            console.error('Erro ao atualizar portão:', error);
            setNotification({ type: 'error', message: 'Erro ao salvar portão.' });
        }
    };

    const handlePromoteToDirector = async (userId, currentPerfil) => {
        const novoPerfil = currentPerfil === 'diretor' ? 'monitor' : 'diretor';
        try {
            const { error } = await supabase
                .from('usuarios')
                .update({ perfil: novoPerfil })
                .eq('id', userId);

            if (error) throw error;
            loadData();
        } catch (error) {
            console.error('Erro ao alterar perfil:', error);
            alert('Erro ao alterar perfil.');
        }
    };

    const handleRemoveInvite = async (inviteId) => {
        if (!confirm('Deseja cancelar este convite?')) return;
        try {
            const { error } = await supabase
                .from('autorizacoes_equipe')
                .delete()
                .eq('id', inviteId);
            if (error) throw error;
            loadData();
        } catch (error) {
            console.error('Erro ao remover convite:', error);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center bg-white p-6 rounded-3xl shadow-sm border">
                <div>
                    <h2 className="text-2xl font-black text-gray-900 tracking-tighter">Gestão da Equipe</h2>
                    <p className="text-gray-500 text-sm font-medium">Controle quem tem acesso ao sistema da escola</p>
                </div>
                {!showAddForm && (
                    <Button variant="primary" onClick={() => setShowAddForm(true)}>
                        + Adicionar Membro
                    </Button>
                )}
            </div>

            {notification && !showAddForm && (
                <div className={`p-4 rounded-2xl text-sm font-bold animate-fade-in text-center mb-6 ${notification.type === 'success'
                    ? 'bg-green-50 text-green-600 border-2 border-green-100'
                    : 'bg-red-50 text-red-600 border-2 border-red-100'
                    }`}>
                    {notification.type === 'success' ? '✨ ' : '⚠️ '}
                    {notification.message}
                </div>
            )}

            {showAddForm && (
                <Card className="animate-scale-in border-2 border-primary/20">
                    <form onSubmit={handleAddMember} className="space-y-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="font-black text-lg tracking-tighter">Adicionar Membro à Equipe</h3>
                            <button type="button" onClick={() => setShowAddForm(false)} className="text-gray-400">✕</button>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">E-mail do Usuário Já Cadastrado</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                                    className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent focus:border-primary rounded-2xl font-bold transition-all"
                                    placeholder="email@escola.com"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Nível de Acesso</label>
                                <select
                                    value={formData.perfil}
                                    onChange={(e) => setFormData(p => ({ ...p, perfil: e.target.value }))}
                                    className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent focus:border-primary rounded-2xl font-bold transition-all"
                                >
                                    <option value="monitor">🛡️ Monitor (Apenas Fila)</option>
                                    <option value="diretor">🚀 Diretor (Acesso Total)</option>
                                </select>
                            </div>
                            {formData.perfil === 'monitor' && (
                                <div className="sm:col-span-2">
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Portão de Atuação</label>
                                    <select
                                        value={formData.portao_id}
                                        onChange={(e) => setFormData(p => ({ ...p, portao_id: e.target.value }))}
                                        className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent focus:border-primary rounded-2xl font-bold transition-all"
                                        required
                                    >
                                        <option value="">Selecione um portão...</option>
                                        {gates.map(g => (
                                            <option key={g.id} value={g.id}>🚪 {g.nome}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                        {notification && (
                            <div className={`p-4 rounded-2xl text-sm font-bold animate-bounce mb-4 text-center ${notification.type === 'success'
                                ? 'bg-green-50 text-green-600 border-2 border-green-100'
                                : 'bg-red-50 text-red-600 border-2 border-red-100'
                                }`}>
                                {notification.type === 'success' ? '✨ ' : '⚠️ '}
                                {notification.message}
                            </div>
                        )}

                        {!notification && (
                            <>
                                <div className="flex gap-3">
                                    <Button variant="outline" type="button" onClick={() => setShowAddForm(false)} className="flex-1 rounded-2xl">Cancelar</Button>
                                    <Button variant="primary" type="submit" loading={adding} className="flex-2 rounded-2xl">Confirmar e Vincular</Button>
                                </div>
                                <p className="text-[10px] text-gray-400 font-bold uppercase text-center mt-2 italic">
                                    * O e-mail deve ser o mesmo usado pelo monitor para criar a conta.
                                </p>
                            </>
                        )}
                    </form>
                </Card>
            )}

            {loading ? (
                <div className="text-center py-10">Carregando equipe...</div>
            ) : (
                <div className="space-y-4">
                    {/* Lista de Convites Ativos */}
                    {invites.length > 0 && (
                        <div className="space-y-3">
                            <h3 className="text-[10px] font-black text-amber-500 uppercase tracking-widest ml-1">Aguardando Cadastro:</h3>
                            {invites.map(i => (
                                <Card key={i.id} className="flex items-center justify-between border-dashed border-amber-200 bg-amber-50/30">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center text-xl">⏳</div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 leading-tight">{i.email}</h3>
                                            <p className="text-[9px] text-amber-600 font-black uppercase tracking-widest">
                                                Convite Enviado • {i.perfil === 'diretor' ? 'Diretor' : 'Monitor'}
                                            </p>
                                        </div>
                                    </div>
                                    <button onClick={() => handleRemoveInvite(i.id)} className="text-xs font-bold text-red-400 hover:text-red-500 p-2">
                                        Cancelar
                                    </button>
                                </Card>
                            ))}
                        </div>
                    )}

                    {/* Lista de Usuários Ativos */}
                    <div className="space-y-3">
                        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Equipe Ativa:</h3>
                        {users.map(u => (
                            <Card key={u.id} className="flex items-center justify-between group hover:border-primary/30 transition-all">
                                <div className="flex items-center gap-4">
                                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-xl ${u.perfil === 'diretor' ? 'bg-primary text-white shadow-lg' : 'bg-gray-100 text-gray-400'}`}>
                                        {u.nome_completo?.[0] || 'U'}
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-900">{u.nome_completo}</h3>
                                        <div className="flex items-center gap-2">
                                            <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest px-2 py-0.5 bg-gray-50 rounded border">
                                                {u.perfil === 'diretor' ? '🚀 Diretor' : '🛡️ Monitor'}
                                            </p>
                                            {u.perfil === 'monitor' && (
                                                <select
                                                    value={u.portao_id || ''}
                                                    onChange={(e) => handleUpdateGate(u.id, e.target.value)}
                                                    className="text-[10px] text-primary font-black uppercase tracking-widest px-2 py-0.5 bg-primary/5 rounded border border-primary/20 focus:ring-1 focus:ring-primary outline-none transition-all cursor-pointer"
                                                >
                                                    <option value="">🚪 Sem Portão</option>
                                                    {gates.map(g => (
                                                        <option key={g.id} value={g.id}>🚪 {g.nome}</option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handlePromoteToDirector(u.id, u.perfil)}
                                        className="text-[10px] font-black uppercase tracking-widest px-4 py-2.5 rounded-xl bg-gray-50 hover:bg-gray-100 text-gray-600 transition-all border border-gray-100"
                                    >
                                        {u.perfil === 'diretor' ? 'Tirar Direção' : 'Tornar Diretor'}
                                    </button>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
